import {
    Button,
    HOT_KEY,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Typography,
    useHotKey,
    useTheme,
} from '@web-chat-core/ui-kit';
import ModalOverlay from '@web-chat-core/ui-kit/core/ModalOverlay';
import { useHotKeyIsolate } from '@web-chat-core/ui-kit/core/HotKeys';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { selectIsModalOpened, selectLocalParticipant, selectModalDataData, selectRoom } from 'state/selectors';
import { ModalName } from 'features/Modals/ModalProvider';
import { useModal } from 'useCase/modal/useModal';
import { getModerationConfirmTexts } from './utils/getModerationConfirmTexts';
import { useModerationConfirmActions } from './hooks/useModerationConfirmActions';
import { ModerationConfirmState } from './types';

export const ModerationConfirmModal = observer(() => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { closeModal } = useModal();

    const confirm = selectModalDataData() as ModerationConfirmState;
    const room = selectRoom();
    const localParticipant = selectLocalParticipant();
    const isOpen = selectIsModalOpened(ModalName.MODERATION_CONFIRM) && !!confirm;

    const { onConfirm } = useModerationConfirmActions({
        confirm,
        room,
        localParticipant,
        onClose: closeModal,
    });

    useHotKeyIsolate({ enabled: () => isOpen });

    useHotKey(
        HOT_KEY.ESC,
        closeModal,
        {
            enabled: () => isOpen,
        },
        [isOpen],
    );

    useHotKey(
        HOT_KEY.ENTER,
        onConfirm,
        {
            enabled: () => isOpen,
        },
        [isOpen],
    );

    if (!isOpen || !confirm) return null;

    const { title, description, confirmButtonLabel } = getModerationConfirmTexts(confirm, t);

    return (
        <ModalOverlay name="moderation-confirm" onClick={closeModal} isNeedOverlay={true}>
            <ModalBody
                width="400px"
                style={{
                    background: theme.root.colors.background.sheetOrModal,
                }}
            >
                <ModalHeader title={title} showCloseButton={true} onClose={closeModal} />
                <ModalContent>
                    <Typography variant="body3" colorVariant="second">
                        {description}
                    </Typography>
                </ModalContent>
                <ModalFooter>
                    <Button variant="secondary" onClick={closeModal}>
                        {t('web_calls_cancel_share_screen')}
                    </Button>
                    <Button onClick={onConfirm}>{confirmButtonLabel}</Button>
                </ModalFooter>
            </ModalBody>
        </ModalOverlay>
    );
});
